import { useDocumentTitle } from "@uidotdev/usehooks";
import React, { ChangeEvent } from "react";
import { twMerge } from "tailwind-merge";

import { StandardContent } from "@/components/App";
import ErrorText from "@/components/ErrorText";
import Header from "@/components/Header";
import Link from "@/components/Link";
import Loader from "@/components/Loader";
import { SubHeader } from "@/components/SubHeader";
import { ExternalLinks } from "@/constants/links";
import { ErrorMessages } from "@/constants/messages";
import { PageTitle } from "@/constants/pages";
import {
  useCommunicationSettings,
  useUpdateCommunicationSettingsMutation,
} from "@/hooks/user";
import {
  ChannelAttribute,
  CommunicationSettingsChannel,
  CommunicationSettingsChannelItem,
  UpdateCommunicationSettingsPayload,
} from "@/services/user";

export function CommunicationPreferencesPage() {
  return (
    <StandardContent>
      <Content />
    </StandardContent>
  );
}

function Content() {
  const communicationSettingsQuery = useCommunicationSettings();
  const updateCommunicationSettingsMutation =
    useUpdateCommunicationSettingsMutation();

  useDocumentTitle(PageTitle.COMMUNICATION_PREFERENCES);

  function onChange(
    channel: CommunicationSettingsChannel,
    attribute: ChannelAttribute,
    value: boolean,
  ) {
    const payload: UpdateCommunicationSettingsPayload = {};
    switch (channel) {
      case "email": {
        payload.email = {
          [attribute]: value,
        };
        break;
      }
      case "text": {
        payload.text = {
          [attribute]: value,
        };
        break;
      }
    }

    updateCommunicationSettingsMutation.mutate({
      payload: payload,
    });
  }

  if (communicationSettingsQuery.isError) {
    return <ErrorText error={ErrorMessages.GENERIC} center />;
  }

  if (communicationSettingsQuery.isPending) {
    return <Loader />;
  }

  return (
    <div className={"flex flex-1 flex-col"}>
      <Header className={"mb-8"}>Communication Preferences</Header>
      <div className={"flex flex-1 flex-col gap-8"}>
        <ChannelRow
          channel={"email"}
          item={communicationSettingsQuery.data.email}
          onCheckboxChange={(attribute, value) => {
            onChange("email", attribute, value);
          }}
          updateLoading={updateCommunicationSettingsMutation.isPending}
        />
        <ChannelRow
          channel={"text"}
          item={communicationSettingsQuery.data.text}
          onCheckboxChange={(attribute, value) => {
            onChange("text", attribute, value);
          }}
          updateLoading={updateCommunicationSettingsMutation.isPending}
        />
      </div>
      <div className={"mt-[100%] flex flex-col"}>
        <p className={"font-sans italic"}>
          By enabling text and/or email preferences, you agree to Restore&apos;s{" "}
          <Link href={ExternalLinks.TERMS} target="_blank">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link href={ExternalLinks.PRIVACY_POLICY} target="_blank">
            Privacy Policy
          </Link>{" "}
          and consent to receive marketing and appointment communications from
          Restore via email and/or text.
        </p>
      </div>
    </div>
  );
}

type OnCheckboxChange = (attribute: ChannelAttribute, value: boolean) => void;

function ChannelRow(props: {
  channel: CommunicationSettingsChannel;
  item: CommunicationSettingsChannelItem;
  onCheckboxChange: OnCheckboxChange;
  updateLoading: boolean;
}) {
  let header;
  switch (props.channel) {
    case "email":
      header = "Email";
      break;
    case "text":
      header = "Text";
      break;
  }

  function onChange(
    event: ChangeEvent<HTMLInputElement>,
    attribute: ChannelAttribute,
  ) {
    props.onCheckboxChange(attribute, event.target.checked);
  }

  return (
    <div className={"flex flex-col"}>
      <SubHeader className={"font-semibold"}>{header}</SubHeader>
      <div className={"flex flex-col py-2"}>
        <div className={"flex flex-row justify-between border-y py-4"}>
          <label
            htmlFor={props.channel + "-promo"}
            className={"w-full cursor-pointer"}
          >
            {props.item.promoDescription}
          </label>
          <input
            id={props.channel + "-promo"}
            type="checkbox"
            checked={props.item.allOff ? false : props.item.promo}
            onChange={(event) => onChange(event, "promo")}
            className={twMerge(
              "h-5 w-5 border-gray-300 accent-amalfi-coast",
              props.updateLoading && "pointer-events-none",
            )}
          />
        </div>
        <div className={"flex flex-col border-b py-4"}>
          <div className={"flex flex-row justify-between"}>
            <label
              htmlFor={props.channel + "-schedule"}
              className={"w-full cursor-pointer"}
            >
              {props.item.scheduleDescription}
            </label>
            <input
              id={props.channel + "-schedule"}
              type="checkbox"
              checked={props.item.allOff ? false : props.item.schedule}
              onChange={(event) => onChange(event, "schedule")}
              className={twMerge(
                "h-5 w-5 border-gray-300 accent-amalfi-coast",
                props.updateLoading && "pointer-events-none",
              )}
            />
          </div>
          {props.channel === "text" ? (
            <div className={"mt-4 text-xs"}>
              <p>
                If you previously texted STOP, please text START to resume
                receiving appointment notifications.
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
